import { useRef, useEffect, useState } from 'react';
import ScrollReveal from '@/components/shared/ScrollReveal';
import {
  ArrowRight,
  Shield,
  CalendarDays,
  SlidersHorizontal,
  Headset,
  Infinity as InfinityIcon,
  Tag,
  PiggyBank,
} from 'lucide-react';

const INSURANCE_QUOTE_URL =
  'https://seguros.igreenenergy.com.br?t=UUueTsQg_laEeYlYwDTzxkj7L4x_0NYhV5YUcwuAXpRobVLRBJML1LSCBs2X';

const benefits = [
  {
    icon: CalendarDays,
    title: 'Pagamento mensal',
    description: 'Boleto mensal sem comprometer o limite do cartão.',
  },
  {
    icon: SlidersHorizontal,
    title: 'Planos flexíveis',
    description: 'Seguro moldado de acordo com a sua realidade.',
  },
  {
    icon: Headset,
    title: 'Segurança e agilidade',
    description: 'Atendimento rápido, em poucos segundos.',
  },
  {
    icon: InfinityIcon,
    title: 'Sem fidelidade',
    description: 'Você permanece enquanto estiver satisfeito.',
  },
  {
    icon: Tag,
    title: 'iGreen Club',
    description: 'Descontos em mais de 600 mil produtos e serviços em todo o Brasil.',
  },
  {
    icon: PiggyBank,
    title: 'Cashback Seguros',
    description: 'Indique novos clientes, aumente seu desconto e até mesmo zere o valor da proteção.',
  },
];

function BenefitCard({ item, index }: { item: typeof benefits[0]; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`group flex h-full items-start gap-3 p-3 md:p-4 rounded-xl bg-white border border-background-200/70 transition-all duration-500 hover:-translate-y-0.5 hover:shadow-md hover:border-accent-500/30 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
      }`}
      style={{ transitionDelay: `${index * 80}ms` }}
    >
      <div className="w-12 h-12 rounded-xl bg-accent-100/80 ring-1 ring-accent-200/50 flex items-center justify-center text-accent-600 shrink-0 transition-transform duration-300 group-hover:scale-110">
        <item.icon className="w-6 h-6" />
      </div>
      <div className="min-w-0">
        <h4 className="text-sm md:text-base font-bold text-foreground-950 mb-0.5">{item.title}</h4>
        <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{item.description}</p>
      </div>
    </div>
  );
}

export default function InsuranceSection() {
  return (
    <section id="seguro" className="immersive-panel bg-background-50">
      <div className="split-viewport">
        <div className="contents">
          {/* Content */}
          <ScrollReveal className="split-viewport__content order-2 lg:order-1 text-center lg:text-left">
            <span className="inline-flex w-auto self-center lg:self-start items-center gap-2 px-4 py-1.5 rounded-full bg-accent-100 border border-accent-200 text-accent-700 text-sm font-semibold mb-5">
              <Shield className="w-4 h-4" />
              Seguro Veicular
            </span>

            <h2 className="font-heading text-2xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 tracking-tighter leading-[1.1]">
              Proteção para o <br className="hidden md:inline" /><span className="text-gradient">seu veículo</span>
            </h2>

            <p className="mt-4 md:mt-5 text-base md:text-lg text-foreground-600 leading-relaxed">
              Planos flexíveis, pagamento mensal e atendimento ágil.
            </p>

            {/* Benefit grid */}
            <div className="mt-6 md:mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
              {benefits.map((item, i) => (
                <BenefitCard key={item.title} item={item} index={i} />
              ))}
            </div>

            {/* CTA */}
            <div className="mt-6 md:mt-8">
              <a
                href={INSURANCE_QUOTE_URL}
                target="_blank"
                rel="noreferrer"
                className="btn-primary mx-2 w-auto max-w-full whitespace-normal px-4 py-3.5 text-center text-sm leading-snug sm:mx-0 sm:px-5 md:px-8 md:py-4 md:text-base group"
              >
                Simular uma cotação sem compromisso
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </a>
              <p className="mt-3 text-xs text-foreground-500 leading-relaxed max-w-lg mx-auto lg:mx-0">
                Operado por BP Seguradora S.A. | Código SUSEP 01546 | CNPJ 50.180.527/0001-13
              </p>
            </div>
          </ScrollReveal>

          {/* Image */}
          <ScrollReveal className="split-viewport__media order-1 lg:order-2" delay={120}>
            <div className="relative h-full">
              <div className="relative h-full overflow-hidden bg-background-100">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20professional%20lifestyle%20photograph%20of%20a%20friendly%20insurance%20consultant%20in%20business%20casual%20attire%20shaking%20hands%20with%20a%20customer%20next%20to%20a%20modern%20silver%20car%20parked%20inside%20a%20bright%20contemporary%20showroom%2C%20both%20smiling%20warmly%2C%20natural%20daylight%2C%20clean%20environment%20with%20subtle%20green%20accents%20and%20glass%20surfaces%2C%20professional%20corporate%20photography%2C%20soft%20shadows%2C%20high%20detail&width=900&height=1200&seq=seguro-lifestyle-01&orientation=portrait"
                  alt="Cliente conversando com um especialista em seguro veicular ao lado de um carro"
                  title="Proteção veicular com a iGreen Energy"
                  className="h-full w-full object-cover object-center"
                  width="900"
                  height="1200"
                />
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}
